import $ from 'jquery';
import ComponentHelper from './ComponentHelper';
import YoutubeHelper from './YoutubeHelper';
import HTML5VideoHelper from './HTML5VideoHelper';

class LightboxHelper {
    constructor() {
        this.componentHelper = new ComponentHelper();
        this.youtubeHelper = new YoutubeHelper();
        this.videoHelper = new HTML5VideoHelper();
    }

    /**
     * if no lightbox exists, init one by calling
     * creating function and function for adding
     * an event listener to the close button
     * @returns {*}
     */
    initLightbox() {
        let lightbox = document.getElementById('lightbox');
        if (!lightbox) {
            lightbox = LightboxHelper.addLightboxHTML();
            this.addCloseButtonEventHandler(lightbox);
        }
        return lightbox;
    }

    /**
     * add eventhandler to lightbox trigger;
     * open lightbox, empty it, add content to it and set focus
     * @param trigger
     * @param lightbox
     */
    initLightboxEventHandler(trigger, lightbox) {
        if (trigger.classList.contains('lightbox-trigger--on-load')) {
            // element to focus if lightbox is closed
            window.lightboxOpener = document.querySelector('.logo');

            this.openLightbox(lightbox, trigger);
            LightboxHelper.emptyLightbox();
            this.addContentToLightbox(trigger);
            this.setKeyboardFocusToLightbox(lightbox);

            return;
        }

        trigger.addEventListener('click', (event) => {
            event.preventDefault();

            // save element to focus if lightbox is closed
            window.lightboxOpener = event.currentTarget;

            this.openLightbox(lightbox, trigger);
            LightboxHelper.emptyLightbox();
            this.addContentToLightbox(event.currentTarget);
            this.setKeyboardFocusToLightbox(lightbox);
        });
    }

    /**
     * set focus to lightbox and add eventlistener for tab key
     */
    setKeyboardFocusToLightbox(lightbox) {
        setTimeout(() => {
            document.removeEventListener('keydown', LightboxHelper.setFocusTrap);

            const lightboxContentContainer = lightbox.querySelector('.lightbox__content-container');

            if (lightboxContentContainer) {
                lightboxContentContainer.setAttribute('tabindex', '-1');
                lightboxContentContainer.focus();
                lightboxContentContainer.removeAttribute('tabindex');

                document.addEventListener('keydown', LightboxHelper.setFocusTrap);
            }
        }, 100);
    }

    /**
     * keep focus inside lightbox
     * @param event
     */
    static setFocusTrap(event) {
        const tabbableElements = `a[href], area[href], input:not([disabled]),
            select:not([disabled]), textarea:not([disabled]),
            button:not([disabled]), iframe`;

        let container = document.getElementById('lightbox');

        if (!container) {
            container = document.querySelector('.popup');
        }

        if (container) {
            const allTabbableElements = container.querySelectorAll(tabbableElements);
            const firstTabbableElement = allTabbableElements[0];
            const lastTabbableElement = allTabbableElements[allTabbableElements.length - 1];

            const keyCode = event.which || event.keyCode;

            // if Tab
            if (keyCode === 9) {
                // Move focus to first element that can be tabbed if Shift isn't used
                if (event.target === lastTabbableElement && !event.shiftKey) {
                    event.preventDefault();
                    window.requestAnimationFrame(() => {
                        firstTabbableElement.focus();
                    });
                } else if (event.target === firstTabbableElement && event.shiftKey) {
                    // Move focus to last element that can be tabbed if Shift is used
                    event.preventDefault();
                    window.requestAnimationFrame(() => {
                        lastTabbableElement.focus();
                    });
                }
            }
        }
    }

    /**
     * @param activatedTrigger
     */
    addContentToLightbox(activatedTrigger) {
        const containerClass = 'lightbox__content';
        const lightboxContent = activatedTrigger.getAttribute('data-lightbox-content');
        const youtubeWrapperId = activatedTrigger.getAttribute('data-lightbox-content-id');
        const lightboxContentType = activatedTrigger.getAttribute('data-lightbox-content-type');
        const lightboxContentWrapper = document.querySelector(`.${containerClass}`);

        switch (lightboxContentType) {
            case 'ajax': {
                const sourceUrl = activatedTrigger.getAttribute('href');

                $.ajax({
                    url: sourceUrl,
                }).done((data) => {
                    if (!data) {
                        return;
                    }
                    lightboxContentWrapper.innerHTML = data;

                    this.componentHelper.initializeDataComponents(lightboxContentWrapper);
                });

                break;
            }
            case '2': {
                const image = document.createElement('img');
                image.src = LightboxHelper.getLightboxContent(activatedTrigger);

                const caption = activatedTrigger.getAttribute('data-lightbox-content-caption');
                const captionElement = document.createElement('div');
                if (caption) {
                    captionElement.innerHTML = caption;
                }

                lightboxContentWrapper.innerHTML = '';
                lightboxContentWrapper.appendChild(image);
                lightboxContentWrapper.appendChild(captionElement);

                break;
            }
            case '4': {
                const videoURL = lightboxContent;

                if (videoURL.indexOf('youtube') >= 0) {
                    const youtubeId = YoutubeHelper.youTubeGetID(videoURL);
                    this.youtubeHelper.addAndAutoplayLightboxVideo(containerClass, youtubeId, youtubeWrapperId);
                } else {
                    this.videoHelper.addAndAutoplayVideo(containerClass, videoURL, true, true);
                }

                break;
            }
            default: {
                lightboxContentWrapper.innerHTML = '';
                lightboxContentWrapper.append(LightboxHelper.getLightboxContent(activatedTrigger));

                break;
            }
        }
    }

    /**
     * get content for lightbox from data attribute
     * @param trigger
     * @returns {*}
     */
    static getLightboxContent(trigger) {
        return trigger.getAttribute('data-lightbox-content');
    }

    /**
     * create lightbox element and append to body
     * @returns {HTMLDivElement}
     */
    static addLightboxHTML() {
        const lightboxElement = document.createElement('div');
        const lightboxCloseText = document.querySelector('.page-header').getAttribute('data-lightbox-close');
        lightboxElement.className = 'lightbox';
        lightboxElement.id = 'lightbox';
        lightboxElement.ariaHidden = 'true';
        lightboxElement.innerHTML = `<div class="lightbox__container">
                <div class="lightbox__content-container">
                    <button class="lightbox__close" aria-label="${lightboxCloseText}"><span></span></button>
                    <div class="lightbox__content">
                        <div class="lightbox__loading-content"><div class="loading-icon"></div></div>
                    </div>
                </div>
            </div>`;

        document.body.appendChild(lightboxElement);

        return lightboxElement;
    }

    /**
     * remove content from lightbox
     */
    static emptyLightbox() {
        const lightboxContent = document.querySelector('.lightbox__content');
        lightboxContent.innerHTML = '';
    }

    /**
     * opens lightbox and adds event listener for
     * closing the lightbox when a click anywhere
     * else on the page occurs
     * @param lightbox
     * @param trigger
     */
    openLightbox(lightbox, trigger) {
        requestAnimationFrame(() => {
            lightbox.removeAttribute('aria-hidden');
            lightbox.classList.add('open');
            lightbox.ariaLabel = trigger.getAttribute('data-lightbox-label');
            lightbox.role = 'dialog';
            document.body.classList.add('no-scroll');

            this.addBodyEventHandler(lightbox);
            document.addEventListener('keydown', this.addEscListener);
        });
    }

    addEscListener(event) {
        const keyCode = event.which || event.keyCode;
        if (keyCode === 27) {
            const activeLightbox = document.getElementsByClassName('lightbox open')[0];
            LightboxHelper.closeLightbox(activeLightbox);
        }
    }

    /**
     * event listener for close button
     * @param lightbox
     */
    addCloseButtonEventHandler(lightbox) {
        lightbox.querySelector('.lightbox__close').addEventListener('click', () => {
            LightboxHelper.closeLightbox(lightbox);
            document.removeEventListener('click', this.bodyClickListener);
        });
    }

    /**
     * add eventlistener to body for closing lightbox
     * @param lightbox
     */
    addBodyEventHandler(lightbox) {
        if (lightbox.classList.contains('open')) {
            requestAnimationFrame(() => {
                document.addEventListener('click', this.bodyClickListener);
            });
        }
    }

    /**
     * close lightbox on click outside
     * @param event
     */
    bodyClickListener(event) {
        const { target } = event;
        const isLightbox = target.closest('.lightbox__content-container')
            || document.querySelector('.lightbox__content-container') === target;

        if (!isLightbox) {
            const activeLightbox = document.getElementsByClassName('lightbox open')[0];
            LightboxHelper.closeLightbox(activeLightbox);
        }
    }

    /**
     * close lightbox and set focus back to lightbox trigger
     * @param lightbox
     */
    static closeLightbox(lightbox) {
        if (lightbox) {
            if (lightbox.classList.contains('open')) {
                const event = new CustomEvent('lightboxclose');
                window.dispatchEvent(event);

                lightbox.setAttribute('aria-hidden', 'true');
                lightbox.removeAttribute('aria-label');
                lightbox.removeAttribute('role');

                // handle visible changes for closing lightbox
                lightbox.classList.remove('open');
                document.body.classList.remove('no-scroll');

                // focus lightbox trigger
                window.lightboxOpener.focus();

                // if lightbox contained players, destroy them
                if (window.lightboxPlayer && window.lightboxPlayer.length) {
                    window.lightboxPlayer.destroy();
                }

                // remove lightbox content
                const contentWrapper = document.querySelector('.lightbox__content');
                contentWrapper.innerHTML = '';

                // remove all event listeners
                document.removeEventListener('click', this.bodyClickListener);
                document.removeEventListener('keydown', LightboxHelper.setFocusTrap);
                document.removeEventListener('keydown', this.addEscListener);
            }
        }
    }
}

export default LightboxHelper;
